import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ClientDetails } from './ClientDetails';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import CenteredSpinner from './CenteredSpinner';

export const Clients = () => {
  const location = useLocation();
  const notificationBuyerId = location?.state?.notificationBuyerId;
  const notificationBuyerRef = location?.state?.notificationBuyerRef;
  const [search, setSearch] = useState(notificationBuyerRef || '');
  const [staffMemberId] = useState(
    localStorage.getItem('staffMemberId'),
  );
  const [searchPastBuyer, setSearchPastBuyer] = useState(null);
  //PastBuyerRef will always have a reference of searchPastBuyer value
  const pastBuyerRef = useRef();
  pastBuyerRef.current = searchPastBuyer;
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'My Clients';
    window.scrollTo(0, 0);
    refetch();
  }, [searchPastBuyer]);

  useEffect(() => {
    if (notificationBuyerRef) {
      setSearch(notificationBuyerRef);
    }
  }, [notificationBuyerRef]);

  const fetchClients = () => {
    const options = {
      method: 'POST',
      url: '/buyer/getmobilebuyerlist',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        negotiatorId: staffMemberId,
        searchPastBuyers: pastBuyerRef.current,
      }),
    };

    return axios
      .request(options)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error('Server error');
      });
  };

  const handleSearchPastBuyerChange = (event) => {
    event.preventDefault();
    event.persist();
    if (searchPastBuyer == event.target.dataset.searchpastbuyer) {
      setSearchPastBuyer(null);
      return;
    }
    setSearchPastBuyer(event.target.dataset.searchpastbuyer);
  };

  const {
    isLoading: isLoadingClients,
    isError,
    data: ClientsData,
    refetch
  } = useQuery('ClientsData', fetchClients, {
    refetchOnWindowFocus: false,
    enabled: false // turned off by default, manual refetch is needed
  });

  const filterClient = (clients, searchValue) => {
    if (!Array.isArray(clients)) {
      return clients;
    }

    const targetBuyerId = Number(notificationBuyerId || 0);
    const targetBuyerRef = String(notificationBuyerRef || '')
      .trim()
      .toLowerCase();
    const normalizedSearch = String(searchValue || '')
      .trim()
      .toLowerCase();

    return clients.filter((client) => {
      const buyerRef = String(
        client?.buyerRef ||
          client?.buyerSpec?.buyerSpecRef ||
          client?.addressRef ||
          '',
      ).toLowerCase();
      const buyerId = Number(client?.buyerId || 0);
      const buyerSpecId = Number(
        client?.buyerSpecId || client?.buyerSpec?.id || 0,
      );

      if (targetBuyerId || targetBuyerRef) {
        return (
          (targetBuyerId &&
            (buyerId === targetBuyerId ||
              buyerSpecId === targetBuyerId)) ||
          (targetBuyerRef && buyerRef.includes(targetBuyerRef))
        );
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        client?.address?.addressIndividualFirstName,
        client?.address?.addressIndividualSurname,
        client?.address?.addressOrganisationName,
        client?.buyerSpec?.buyerSpecName,
        buyerRef,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        );
    });
  };

  const filterClients = filterClient(ClientsData, search);

  if (isLoadingClients) {
    return <CenteredSpinner />;
  }

  if (isError) {
    return (
      <div className="container">
        <div className="row justify-content-center align-items-center vh-100">
          <h1>Server is down, please try again later </h1>
        </div>
      </div>
    );
  }

  const clientCount = filterClients?.length ?? 0;

  return (
    <div className="container pt-3 pb-4">
      <div className="card shadow-sm border-0 overflow-hidden property-list-card">
        <div className="property-list-hero d-flex align-items-center px-4 py-3">
          <div className="property-list-hero-icon d-flex align-items-center justify-content-center">
            <i className="fas fa-users" aria-hidden="true"></i>
          </div>
          <div className="ml-3">
            <p className="mb-1 text-uppercase text-white-50 small">Clients</p>
            <h4 className="mb-0 text-white">Your client list.</h4>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm ml-auto property-list-back"
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back
          </button>
        </div>

        <div className="p-4">
          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="property-list-count">{clientCount}</div>
              <div className="ml-3">
                <p className="mb-1 text-muted small">Clients found</p>
                <h5 className="mb-0">Search and filter your contacts.</h5>
              </div>
            </div>
            <div className="d-flex property-filter-group mt-3 mt-lg-0">
              <button
                type="button"
                className={`property-filter-pill ${
                  searchPastBuyer !== 'true' ? 'property-filter-pill--active' : ''
                }`}
                onClick={handleSearchPastBuyerChange}
                data-searchPastBuyer="false"
              >
                Current Clients
              </button>
              <button
                type="button"
                className={`property-filter-pill ${
                  searchPastBuyer === 'true' ? 'property-filter-pill--active' : ''
                }`}
                onClick={handleSearchPastBuyerChange}
                data-searchPastBuyer="true"
              >
                Past Clients
              </button>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12 col-md-7 mb-3">
              <div className="form-group has-search w-100 property-list-search">
                <span className="fa fa-search form-control-feedback"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {clientCount < 1 ? (
            <h4 className="text-center text-muted">No search results found</h4>
          ) : null}

          <div className="mt-3">
            {filterClients &&
              React.Children.toArray(
                filterClients?.map((contact) => (
                  <ClientDetails contact={{ contact }} />
                )),
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

